import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { setGlobalChatDrawer } from "../utils/chatUtils";
import customhistory from "../history/customhistory";

interface ChatDrawerContextType {
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  openChatWithUser: (userId: string, userEmail: string) => void;
  openChatWithChatId: (
    chatId: string,
    senderId: string,
    senderEmail: string
  ) => void;
  selectedUserId: string | null;
  selectedUserEmail: string | null;
  selectedChatId: string | null;
}

const ChatDrawerContext = createContext<ChatDrawerContextType | undefined>(
  undefined
);

export const useChatDrawer = () => {
  const context = useContext(ChatDrawerContext);
  if (!context) {
    throw new Error("useChatDrawer must be used within a ChatDrawerProvider");
  }
  return context;
};

interface ChatDrawerProviderProps {
  children: React.ReactNode;
}

export const ChatDrawerProvider: React.FC<ChatDrawerProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(
    null
  );
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);

  const openDrawer = useCallback(() => {
    console.log("🔔 ChatDrawerContext: Navigating to /chats");
    customhistory.push("/chats");
  }, []);

  const closeDrawer = useCallback(() => {
    console.log("🔔 ChatDrawerContext: Closing drawer");
    setIsOpen(false);
    setSelectedUserId(null);
    setSelectedUserEmail(null);
    setSelectedChatId(null);
  }, []);

  const openChatWithUser = useCallback((userId: string, userEmail: string) => {
    setSelectedUserId(userId);
    setSelectedUserEmail(userEmail);
    setSelectedChatId(null);
    customhistory.push("/chats");
  }, []);

  const openChatWithChatId = useCallback(
    (chatId: string, senderId: string, senderEmail: string) => {
      console.log(
        "🔔 ChatDrawerContext: Navigating to /chats with chatId:",
        chatId,
        "sender:",
        senderEmail
      );
      setSelectedChatId(chatId);
      setSelectedUserId(senderId);
      setSelectedUserEmail(senderEmail);
      customhistory.push("/chats");
    },
    []
  );

  // Set global functions for external access
  useEffect(() => {
    setGlobalChatDrawer(openChatWithUser, openChatWithChatId);
  }, [openChatWithUser, openChatWithChatId]);

  const value: ChatDrawerContextType = {
    isOpen,
    openDrawer,
    closeDrawer,
    openChatWithUser,
    openChatWithChatId,
    selectedUserId,
    selectedUserEmail,
    selectedChatId,
  };

  return (
    <ChatDrawerContext.Provider value={value}>
      {children}
    </ChatDrawerContext.Provider>
  );
};
